/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 이미지 업로드 및 표시를 담당하는 엘리먼트를 정의한다.
 *
 * @file /src/rootin/elements/image/Image.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 9. 9.
 */
import * as parent from '@rootin/base/Element';
import { Block } from '@rootin/base/Blocks';
import { Images } from '@rootin/blocks/images/Images';
import { Window } from '@components/window/Window';
import { Image as ImageField } from '@components/form/field/image/Image';
import { Text as TextField } from '@components/form/field/text/Text';
import { Button } from '@namespaces/components/Button';
import { Ajax } from '@utils/Ajax';
import { Html } from '@utils/Html';
import { Dom } from '@utils/Dom';
import { Language } from '@utils/Language';
import './Image.css';

export interface Properties extends parent.Properties {}

export class Image extends parent.Element {
    /**
     * 엘리먼트타입
     */
    type: string = 'image';

    /**
     * 엘리먼트종류
     */
    kind: 'text' | 'atomic' | 'container' = 'atomic';

    /**
     * 이미지 업로드버튼 DOM
     */
    $upload: Dom = null;

    /**
     * 이미지 업로드 및 표시를 담당하는 엘리먼트를 정의한다.
     *
     * @param block - 이 엘리먼트가 속한 블록
     * @param properties - 엘리먼트설정
     */
    constructor(
        block: Block,
        public properties: Properties
    ) {
        super(block, properties);
    }

    /**
     * 이미지 업로드버튼 DOM 을 가져온다.
     *
     * @returns 이미지 업로드버튼 DOM
     */
    $getUpload(): Dom {
        if (this.$upload === null) {
            this.$upload = Html.create('button', { type: 'button', 'data-action': 'upload' });
            this.$upload.append(
                Html.icon(
                    '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-image-up-icon lucide-image-up"><path d="M10.3 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10l-3.1-3.1a2 2 0 0 0-2.814.014L6 21"/><path d="m14 19.5 3-3 3 3"/><path d="M17 22v-5.5"/><circle cx="9" cy="9" r="2"/></svg>'
                )
            );
            this.$upload.append(Html.create('span').html(Language.get('rootin.elements.image.select')));

            this.$upload.on('mousedown', (_, e) => {
                e.preventDefault();
                e.stopImmediatePropagation();
            });

            this.$upload.on('click', () => {
                if (this.$getElement().getAttr('data-attachment-id')?.length == 36) {
                    return;
                }
                this.getEditor().focus.suspend();
                this.upload();
            });

            this.$upload.on('dblclick', () => {
                if (this.$getElement().getAttr('data-attachment-id')?.length != 36) {
                    return;
                }

                this.getEditor().focus.suspend();
                this.upload();
            });
        }

        return this.$upload;
    }

    /**
     * 이 엘리먼트를 담은 블록을 가져온다.
     * @todo 나중에 Image Element 를 담은 다른 블록이 존재한다면 추가해준다.
     *
     * @returns 이미지 블록
     */
    getImageBlock(): Images {
        return this.block.getParents('images') as Images;
    }

    /**
     * 이미지 블록으로 부터 이미지 비율을 가져온다.
     *
     * @returns 이미지 비율
     */
    getRatio(): [number, number] | null {
        const block = this.getImageBlock();
        if (block === null) {
            // 붙여넣기로 아이템을 복원할 때는 Container 에 실제로 꽂히기 전에
            // `setValue()`(→ `setAttachmentId()`)가 먼저 불린다 — 그 시점엔 아직 부모
            // 이미지블록을 못 찾으므로(실측 확인, TypeError 발생), 비율 제약 없이 원본
            // 크기를 그대로 쓰게 둔다.
            return null;
        }

        const ratio = block.$getBlock().getAttr('data-ratio');

        if (ratio == 'origin') {
            return null;
        }

        const SIZES: { [ratio: string]: [number, number] } = {
            '1:1': [1200, 1200],
            '4:3': [1600, 1200],
            '3:4': [1200, 1600],
            '16:10': [1600, 1000],
            '10:16': [1000, 1600],
        };

        return SIZES[ratio ?? ''] ?? null;
    }

    /**
     * 이미지를 업로드하기 위한 윈도우를 띄운다.
     *
     * @param file - 이미 정해진 파일이 있으면(에디터 위로 드랍된 경우 등) 넘긴다 — 창이 뜬 뒤
     * Field 자신의 드랍존에 드랍된 것처럼 흘려보낸다.
     */
    async upload(file: File | null = null): Promise<void> {
        const ratio = this.getRatio();
        const window = new Window({
            title: Language.get('rootin.elements.image.upload'),
            width: 360,
            modal: true,
            padding: 10,
            items: [
                new ImageField({
                    url: this.getEditor().url,
                    imageWidth: ratio?.at(0) ?? 1200,
                    imageHeight: ratio?.at(1) ?? 1200,
                    showSize: ratio !== null,
                    editable: ratio !== null,
                    autoUpload: false,
                    value: this.$getElement().getAttr('data-attachment-id') ?? null,
                }),
                new TextField({
                    name: 'alt',
                    margin: [8, 0],
                    placeholder: Language.get('rootin.elements.image.alt'),
                    value: this.$getElement().getAttr('data-alt') ?? null,
                }),
            ],
            buttons: [
                new Button({
                    text: Language.get('buttons.cancel'),
                    handler: (button) => {
                        const window = button.getParent().getParent<Window>();
                        window.close();
                    },
                }),
                new Button({
                    text: Language.get('buttons.ok'),
                    buttonClass: 'confirm',
                    handler: async (button) => {
                        const window = button.getParent().getParent<Window>();
                        const image = window.getItemAt<ImageField>(0);
                        const alt = window.getItemAt<TextField>(1);
                        const value = await image.getValue();

                        if (value !== null) {
                            await this.setAttachmentId(value);
                            this.setAlt(await alt.getValue());
                            window.close();
                        }
                    },
                }),
            ],
        });

        window.addEvent('close', () => {
            this.getEditor().focus.resume();
        });

        await window.show();

        if (file !== null) {
            const field = window.getItemAt<ImageField>(0);
            const dataTransfer = new DataTransfer();
            dataTransfer.items.add(file);
            field
                .$getFiles()
                .getEl()
                .dispatchEvent(new DragEvent('drop', { bubbles: true, cancelable: true, dataTransfer }));
        }
    }

    /**
     * 이미지 업로드가 완료된 후 첨부파일고유값을 설정한다.
     *
     * @param attachment_id - 첨부파일고유값
     */
    async setAttachmentId(attachment_id: string): Promise<void> {
        const results = await Ajax.get(this.getEditor().url + '/' + attachment_id);
        if (results.success == true) {
            const attachment = results.attachment ?? null;
            if (attachment !== null) {
                this.$getElement().setAttr('data-attachment-id', attachment_id);

                const ratio = this.getRatio() ?? [attachment.width, attachment.height];

                this.$getElement().setAttr('data-ratio', ratio.join(':'));

                if (this.getRatio() === null) {
                    this.$getElement().setStyle('aspect-ratio', ratio.join(' / '));
                }

                this.$getElement().setStyle('background-image', `url(${attachment.view})`);
            }
        }
    }

    /**
     * 이미지 설명을 설정한다.
     *
     * @param ?string - 이미지서명
     */
    setAlt(alt: string = null): void {
        this.$getElement().setAttr('data-alt', alt);
    }

    /**
     * 엘리먼트를 렌더링한다.
     */
    async render(): Promise<void> {
        this.$getElement().append(this.$getUpload());
    }

    /**
     * 엘리먼트가 비었는지 확인한다.
     *
     * @returns 엘리먼트가 비었는지 여부
     */
    isEmpty(): boolean {
        return this.$getElement().getAttr('data-attachment-id').length != 36;
    }

    /**
     * 엘리먼트값을 가져온다.
     *
     * @returns 엘리먼트값
     */
    getValue(): { attachment_id?: string; alt?: string } | null {
        let attachment_id = this.$getElement().getAttr('data-attachment-id');
        attachment_id = attachment_id.length == 36 ? attachment_id : null;
        return { attachment_id: attachment_id, alt: this.$getElement().getAttr('data-alt') ?? '' };
    }

    /**
     * 엘리먼트값을 지정한다.
     *
     * @param value - 엘리먼트값
     */
    async setValue(value: { attachment_id?: string; alt?: string } | null): Promise<void> {
        if ((value?.attachment_id ?? null) !== null) {
            await this.setAttachmentId(value.attachment_id);
        }

        if ((value?.alt ?? null) !== null) {
            this.setAlt(value.alt);
        }
    }
}
